import { formatIST } from '../../../shared/utils/formatters';
import React, { useEffect, useState } from 'react'
import { Table, Tag, Button, Space, message, Popconfirm } from 'antd'
import { StopOutlined, LogoutOutlined } from '@ant-design/icons'
import { apiClient as api } from '../../../shared/api/axios-instance'

interface ActiveUsersDataGridProps {
    merchantId: string;
}

interface MerchantStaff {
    user_id: number;
    is_active: boolean;
    role_name: string;
    invited_at: string;
    joined_at: string | null;
}

export const ActiveUsersDataGrid: React.FC<ActiveUsersDataGridProps> = ({ merchantId }) => {
    const [staff, setStaff] = useState<MerchantStaff[]>([])
    const [loading, setLoading] = useState(false)

    const fetchStaff = async () => {
        try {
            setLoading(true)
            // Ideally we'd hit admin API to list merchant staff
            // /admin/merchants/{merchantId}/staff
            const response = await api.get(`/admin/merchants/${merchantId}/staff`)
            setStaff(response.data.data || [])
        } catch (error: any) {
            message.error('Failed to load merchant team data.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchStaff()
    }, [merchantId])

    const handleForceSuspend = async (userId: number) => {
        try {
            await api.post(`/api/v1/admin/merchants/${merchantId}/staff/${userId}/suspend`)
            message.success('User access forcefully suspended')
            fetchStaff()
        } catch (error: any) {
            message.error('Failed to suspend user')
        }
    }

    const handleRevokeSession = async (userId: number) => {
        try {
            await api.post(`/api/v1/admin/users/${userId}/revoke-sessions`)
            message.success('All active sessions for this user have been revoked')
        } catch (error: any) {
            message.error('Failed to revoke sessions')
        }
    }

    const columns = [
        {
            title: 'User ID',
            dataIndex: 'user_id',
            key: 'user_id',
        },
        {
            title: 'Role',
            dataIndex: 'role_name',
            key: 'role_name',
            render: (role: string) => <Tag color="blue">{role.toUpperCase()}</Tag>
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'ACTIVE' : 'SUSPENDED'}
                </Tag>
            )
        },
        {
            title: 'Joined At',
            dataIndex: 'joined_at',
            key: 'joined_at',
            render: (val: string) => val ? formatIST(val) : 'Pending Invite'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: MerchantStaff) => (
                <Space>
                    <Popconfirm
                        title="Force Suspend"
                        description="Are you sure you want to suspend this user? This immediately cuts off access."
                        onConfirm={() => handleForceSuspend(record.user_id)}
                        okText="Yes, Suspend"
                        cancelText="Cancel"
                    >
                        <Button 
                            danger 
                            size="small" 
                            icon={<StopOutlined />}
                            disabled={!record.is_active}
                        >
                            Suspend
                        </Button>
                    </Popconfirm>
                    <Popconfirm
                        title="Revoke Sessions"
                        description="This bumps token_version and immediately invalidates active JWTs across devices."
                        onConfirm={() => handleRevokeSession(record.user_id)}
                        okText="Yes, Revoke"
                        cancelText="Cancel"
                    >
                        <Button size="small" icon={<LogoutOutlined />}>
                            Revoke Sessions
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ]

    return (
        <Table
            columns={columns}
            dataSource={staff}
            rowKey="user_id"
            loading={loading}
            pagination={{ pageSize: 10 }}
        />
    )
}
